import { useEffect, useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import { useCsvExport } from "../../lib/useCsvExport";
import { useTableView } from "../../lib/useTableView";
import { TableToolbar, TablePagination } from "../../components/TableControls";
import { ProductPicker } from "../../components/ProductPicker";
import { firstProblem, minLen, isNonNegative } from "../../components/validators";
import type { EngineOverviewDto, EngineRowDto, InventoryAction, ProductCatalogItem } from "../../ipc/types";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

function NewStockForm({ catalog, onCreate, onCancel, busy }: { catalog: readonly ProductCatalogItem[]; onCreate: (v: { sku: string; initialOnHand: number }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [sku, setSku] = useState("");
  const [onHand, setOnHand] = useState("0");
  const onHandNum = Number(onHand);
  const hint = firstProblem([
    { ok: minLen(sku, 2), msg: "Elige un producto (SKU) del catálogo" },
    { ok: isNonNegative(onHand), msg: "La existencia inicial debe ser 0 o más" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo stock" subtitle="Registra un producto de tu catálogo en la bodega con su existencia inicial" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field pd-form__full"><span className="pd-field__label">Producto (SKU)</span>
            <ProductPicker catalog={catalog} value={sku} onChange={setSku} ariaLabel="SKU" /></div>
          <div className="pd-field"><span className="pd-field__label">Existencia inicial</span>
            <input className="pd-input" value={onHand} type="number" min="0" aria-label="Existencia inicial" onChange={(e) => setOnHand(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ sku: sku.trim(), initialOnHand: Math.round(onHandNum) })}>
            {busy ? "Creando…" : "Registrar stock"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function StockRow({ row, busy, onMove }: { row: EngineRowDto; busy: boolean; onMove: (id: string, action: InventoryAction, quantity: number) => void }): JSX.Element {
  const [qty, setQty] = useState("");
  const qtyNum = Number(qty);
  const disabled = busy || !(qtyNum >= 0) || qty.trim() === "";
  return (
    <tr>
      <td><div className="strong">{row.primary}</div></td>
      <td className="muted">{row.secondary}</td>
      <td><Badge tone={row.status.tone} dot>{row.status.label}</Badge></td>
      <td className="strong">{row.amount}</td>
      <td>
        <div className="row" style={{ gap: 6, alignItems: "center" }}>
          <input className="pd-input" value={qty} type="number" min="0" placeholder="Cant." aria-label={`Cantidad ${row.primary}`}
            style={{ width: 74 }} onChange={(e) => setQty(e.target.value)} />
          <Button variant="secondary" size="sm" disabled={disabled || qtyNum <= 0} onClick={() => { onMove(row.id, "receive", qtyNum); setQty(""); }}>Recibir</Button>
          <Button variant="ghost" size="sm" disabled={disabled || qtyNum <= 0} onClick={() => { onMove(row.id, "issue", qtyNum); setQty(""); }}>Despachar</Button>
          <Button variant="ghost" size="sm" disabled={disabled} onClick={() => { onMove(row.id, "adjust", qtyNum); setQty(""); }}>Ajustar a</Button>
        </div>
      </td>
    </tr>
  );
}

export function InventoryScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("inventory"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<readonly ProductCatalogItem[]>([]);
  const { exportCsv, exporting, banner } = useCsvExport(state.status === "ready" ? state.data : { title: "inventario", columns: [], rows: [] });
  const view = useTableView(state.status === "ready" ? state.data.rows : []);

  useEffect(() => {
    let active = true;
    client.getProductCatalog().then((res) => { if (active && res.ok) setCatalog(res.value.items); });
    return () => { active = false; };
  }, [client]);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { sku: string; initialOnHand: number }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createStock(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`SKU "${v.sku}" registrado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo registrar" : res.error.message);
  };
  const move = async (id: string, action: InventoryAction, quantity: number): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.moveStock(id, action, quantity);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Movimiento no permitido" : res.error.message);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="inventory" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Inventario</h1>
              <p className="pagehead__sub">Stock por SKU. Registra existencias y mueve entradas, salidas y ajustes; se guardan en tu equipo.</p>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics }} />
            <Button variant="secondary" size="sm" icon="analytics" disabled={exporting || data.rows.length === 0} onClick={exportCsv}>{exporting ? "Exportando…" : "Exportar CSV"}</Button>
            <Button size="sm" icon="inventory" onClick={() => setShowForm((s) => !s)}>Nuevo stock</Button>
          </div>
        </div>
      </div>

      {banner}

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewStockForm catalog={catalog} onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Existencias" subtitle={`${data.rows.length} SKU`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="inventory" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes stock registrado</div><div className="faint" style={{ fontSize: 13 }}>Registra el primer SKU con “Nuevo stock”.</div></div>
          ) : (
            <>
            <TableToolbar view={view} searchPlaceholder="Buscar por SKU, detalle o estado…" />
            {view.rows.length === 0 ? (
              <div className="statecard"><div className="faint" style={{ fontSize: 13 }}>Ningún SKU coincide con la búsqueda.</div></div>
            ) : (
            <table className="table">
              <thead><tr><th>SKU</th><th>Detalle</th><th>Estado</th><th>On hand</th><th>Movimiento</th></tr></thead>
              <tbody>
                {view.rows.map((r) => <StockRow key={r.id} row={r} busy={busy} onMove={move} />)}
              </tbody>
            </table>
            )}
            <TablePagination view={view} />
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
